import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import {
  BASE_URL,
  LOYALITY_PROGRAM_STORE_LIST,
  SAVE_LOYALITY_PROGRAM,
  ADD_LOYALITY_PROGRAM,
} from "../../../Constants/Config";

const initialState = {
  loading: false,
  saveLoading: false,
  bonusUpsertLoading: false,
  loyaltyProgramStore: null,
  successMessage: "",
  saveSuccessMessage: "",
  bonusUpsertSuccessMessage: "",
  error: "",
  saveError: "",
  bonusUpsertError: "",
};

export const fetchloyaltyProgramStore = createAsyncThunk(
  "loyaltyprogram/fetchloyaltyProgramStore",
  async (data, { rejectWithValue }) => {
    const dataNew = {
      merchant_id: data?.merchantId,
      token_id: data?.tokenId,
      login_type: data?.tokenType,
      group_id: data?.group_id,
    };
    try {
      const response = await axios.post(
        BASE_URL + LOYALITY_PROGRAM_STORE_LIST,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${data?.token}`,
          },
        }
      );

      if (response?.data?.status == true) {
        console.log(response?.data?.loyalty_program_data, "----------------");
        return response?.data?.loyalty_program_data || null;
      }

      return null;
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const saveLoyaltyProgramStore = createAsyncThunk(
  "loyaltyprogram/saveLoyaltyProgramStore",
  async (data, { rejectWithValue }) => {
    const payload = {
      merchant_id: data?.merchantId,
      admin_id: data?.adminId,
      group_id: data?.groupId ?? 0,
      enable_loyalty: data?.enableLoyalty ? "1" : "0",
      current_points: data?.currentPoints,
      points_per_dollar: data?.pointsPerDollar,
      redemption_value: data?.redemptionValue,
      min_points_redemption: data?.minPointsRedemption,
      enable_promotion_id: data?.enabledPromotionIds || "",
      login_employee_id: data?.employeeId ?? null,
      token_id: data?.tokenId,
      login_type: data?.tokenType,
    };

    try {
      const response = await axios.post(BASE_URL + SAVE_LOYALITY_PROGRAM, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${data?.token}`,
        },
      });

      return response?.data;
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const upsertLoyaltyBonusProgram = createAsyncThunk(
  "loyaltyprogram/upsertLoyaltyBonusProgram",
  async (data, { rejectWithValue }) => {
    const payload = {
      ...(data?.promotionId ? { promotion_id: data.promotionId } : {}),
      merchant_id: data?.merchantId,
      loyalty_program_id: data?.loyaltyProgramId ?? 0,
      admin_id: data?.adminId,
      enable_promotion: data?.enablePromotion ? "1" : "0",
      start_date: data?.startDate,
      end_date: data?.endDate,
      current_points: data?.currentPoints,
      promotion_name: data?.promotionName,
      bonus_points: data?.bonusPoints,
      repeat_weekly: data?.repeatWeekly ? "1" : "0",
      active_days: data?.activeDays || "",
      active_hours: data?.activeHours || "00:00:00_23:59:59",
      group_id: data?.groupId ?? 0,
      login_employee_id: data?.employeeId ?? null,
      token_id: data?.tokenId,
      login_type: data?.tokenType,
    };

    try {
      const response = await axios.post(BASE_URL + ADD_LOYALITY_PROGRAM, payload, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${data?.token}`,
        },
      });

      return response?.data;
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const loyaltyprogramStoreSlice = createSlice({
  name: "loyaltyprogram",
  initialState,
  reducers: {
    resetLoyaltyProgramStoreState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(fetchloyaltyProgramStore.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchloyaltyProgramStore.fulfilled, (state, action) => {
      state.loading = false;
      state.loyaltyProgramStore = action.payload || null;
      state.error = "";
    });
    builder.addCase(fetchloyaltyProgramStore.rejected, (state, action) => {
      state.loading = false;
      state.loyaltyProgramStore = null;
      state.error = action.error.message;
    });

    builder.addCase(saveLoyaltyProgramStore.pending, (state) => {
      state.saveLoading = true;
      state.saveError = "";
      state.saveSuccessMessage = "";
    });
    builder.addCase(saveLoyaltyProgramStore.fulfilled, (state, action) => {
      state.saveLoading = false;
      state.saveError = "";
      state.saveSuccessMessage =
        action?.payload?.message || "Loyalty program updated successfully";
    });
    builder.addCase(saveLoyaltyProgramStore.rejected, (state, action) => {
      state.saveLoading = false;
      state.saveSuccessMessage = "";
      state.saveError = action?.payload?.message || action?.error?.message || "Failed to save loyalty program";
    });

    builder.addCase(upsertLoyaltyBonusProgram.pending, (state) => {
      state.bonusUpsertLoading = true;
      state.bonusUpsertError = "";
      state.bonusUpsertSuccessMessage = "";
    });
    builder.addCase(upsertLoyaltyBonusProgram.fulfilled, (state, action) => {
      state.bonusUpsertLoading = false;
      state.bonusUpsertError = "";
      state.bonusUpsertSuccessMessage =
        action?.payload?.message || "Bonus program updated successfully";
    });
    builder.addCase(upsertLoyaltyBonusProgram.rejected, (state, action) => {
      state.bonusUpsertLoading = false;
      state.bonusUpsertSuccessMessage = "";
      state.bonusUpsertError =
        action?.payload?.message ||
        action?.error?.message ||
        "Failed to update bonus program";
    });
  },
});

export const { resetLoyaltyProgramStoreState } = loyaltyprogramStoreSlice.actions;
export default loyaltyprogramStoreSlice.reducer;
